import { Component, Injectable, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "../../../authentication/services/auth.service";
import { AlertService } from "../../../shared/alert/services/alert.service";
import { UrlService } from "../../../shared/services/url.service";
import * as moment from "moment-timezone";
import { PostService } from "../../../shared/services/post.service";
import { DraftSeo, PostSeo, TranslationStatus } from "../../../shared/models/PostSeo";
import { PostTranslationsService } from "../../services/postTranslations.service";
import { PendingTranslation, PendingTranslationStatus } from "../../model/PendingTranslation";
import { APP_SETTINGS } from "../../../shared/models/backofficeConfiguration";
import { PostTranslationsConfig } from "../../model/PostTranslationsConfig";
import { Author } from "../../../shared/models/Author";
import { Tag } from "../../../shared/models/Tag";
import { TranslationRecord } from "../../model/TranslationRecord";

@Component({
  selector: "app-post-translation",
  templateUrl: "../templates/postTranslation.component.html",
})
@Injectable()
export class PostTranslationComponent implements OnInit {
  title = "Post Translation";
  titleType = "title";
  contentType = "content";
  maxTitleChars = 110;
  DashboardUrl: string;
  CurrentLanguage: string;
  SourcePostId: string;
  DraftId: string;
  TranslationId: string;
  PostCreatedLink: string;
  postTags: Tag[] = [];
  authorSelected: Author = {
    AuthorId: null,
    Name: ""
  };

  PostTranslationsConfig: PostTranslationsConfig = APP_SETTINGS.PostTranslationsConfig;

  TranslatedSummary = "";
  TranslatedContent = "";
  TranslatedTitle = "";
  Characters = 0;

  EditionMode = true;
  PreviewMode = false;
  PostCreatedMode = false;
  TranslationInProgress = false;
  CurrentlySendingPost = false;
  ShowTranslationButtons = false;
  ExceededLimit = false;
  SavingAsDraft = false;
  IsDraft = false;
  TranslationSchedulingIntervalId = 0;

  PendingTranslation: PendingTranslation = null;
  PendingTranslationFound?: boolean = null;

  SourcePost: PostSeo = null;
  SourcePostFound?: boolean = null;
  DraftPostFound?: boolean = null;

  SupportedCultures = APP_SETTINGS.SupportedCultures;

  constructor(
    private activatedRoute: ActivatedRoute,
    private urlService: UrlService,
    private alertService: AlertService,
    private authService: AuthService,
    private postService: PostService,
    private postTranslationsService: PostTranslationsService,
  ) { }

  ngOnInit() {
    this.DashboardUrl = this.urlService.GetDashboardRoute();
    this.CurrentLanguage = this.authService.getActualLanguage().cultureNameISO;
    this.showTranslationButton(this.CurrentLanguage);
    this.SetWYSWYGLanguage();
    moment.tz.setDefault("UTC");

    this.SourcePostId = this.activatedRoute.snapshot.params.OriginalPostId;
    this.DraftId = this.activatedRoute.snapshot.queryParams.draftId;
    this.IsDraft = this.DraftId != undefined;

    this.initModel();
  }

  ngOnDestroy () {
    clearInterval(this.TranslationSchedulingIntervalId);
  }

  authorChange(value: Author) {
    this.authorSelected = value;
  }

  changeTitle(value: string) {
    this.Characters = value.length;
    if (this.Characters > this.maxTitleChars) {
      this.ExceededLimit = true;
      document.getElementById("postTitle").classList.add("warning");
    } else if (this.ExceededLimit && this.Characters <= this.maxTitleChars) {
      document.getElementById("postTitle").classList.remove("warning");
      this.ExceededLimit = false;
    }
  }

  loadTags(savedTags: string[]) {
    if (savedTags)
      this.postTags = savedTags.map<{
        Id: string;
        Name: string;
      }>((t) => {
        return {
          Id: t,
          Name: '',
        };
      });
  }

  private initModel() {
    this.loadPending();
    this.loadPost();
  }

  addTag(tag) {
    this.postTags.push(tag);
  }

  removeTag(tag) {
    let indexTag = this.postTags.indexOf(tag);
    if (indexTag != -1) {
      this.postTags.splice(indexTag, 1);
    }
  }

  private showTranslationButton(actualCulture) {
    if (
      this.SupportedCultures &&
      this.SupportedCultures.includes(actualCulture)
    )
      this.ShowTranslationButtons = true;
  }

  private loadPending() {

    const obs = this.postTranslationsService.GetPendingBySourcePostId(this.CurrentLanguage, this.SourcePostId);
    
    obs.subscribe((pendingTranslation) => {
      this.PendingTranslation = pendingTranslation;
      this.PendingTranslationFound = !!pendingTranslation;
      
      if (!this.PendingTranslationFound || this.IsDraft)
        return;

      this.TranslatedContent = pendingTranslation.automaticContentTranslation;
      this.TranslatedTitle = pendingTranslation.automaticTitleTranslation;

      if (pendingTranslation.pendingTranslationStatus != PendingTranslationStatus.inProgress)
        return;

      this.TranslationInProgress = true;
      this.startTranslationScheduleMonitor();
    });
  }

  public translationInProgress(){
    
    return this.TranslationInProgress;
  }
  
  private loadPost() {
    this.postService
      .GetById(this.SourcePostId, this.PostTranslationsConfig.MainLanguageCultureName, true)
      .subscribe((post) => {
        this.SourcePostFound = !!post;

        if (!this.SourcePostFound)
          return;

        this.SourcePost = post;

        if (this.IsDraft)
          return;

        this.authorSelected.AuthorId = this.SourcePost.AuthorId;
        this.loadTags(this.SourcePost.Tags);
      });

    if (!this.IsDraft)
      return;

    this.postService.GetDraftById(this.CurrentLanguage, this.DraftId)
      .subscribe((post) => {
        this.DraftPostFound = !!post;

        if (!this.DraftPostFound)
          return;

        this.TranslatedContent = post.Content;
        this.authorSelected.AuthorId = post.AuthorId;
        this.TranslatedTitle = post.Title
        this.loadTags(post.Tags);
      });
  }

  public translatePost() {

    if (this.TranslationInProgress)
      return;

    this.alertService.add("success", "Post translation in progress, please wait.");

    this.TranslationInProgress = true;

    this.postTranslationsService
      .TranslatePost(this.CurrentLanguage, this.SourcePostId, this.SourcePost.Title, this.SourcePost.Content)
      .subscribe(
        (pendingTranslation) => {
          this.TranslationInProgress = false;

          this.TranslatedContent = pendingTranslation.automaticContentTranslation;
          this.TranslatedTitle = pendingTranslation.automaticTitleTranslation; 
        },
        (err) => {
          if (err.error) this.alertService.add("danger", err.error);
          else
            this.alertService.add(
              "danger",
              "There was a problem initiating translation"
            );
          
          this.TranslationInProgress = false;
        }
      );
  }

  public startTranslationScheduleMonitor() {

    this.TranslationSchedulingIntervalId = setInterval(() => {

      const obs = this.postTranslationsService.GetPendingBySourcePostId(this.CurrentLanguage, this.SourcePostId);

      obs.subscribe((pendingTranslation) => {

        this.PendingTranslation = pendingTranslation;
        this.PendingTranslation.pendingTranslationStatus = pendingTranslation.pendingTranslationStatus;

        if (this.PendingTranslation.pendingTranslationStatus != PendingTranslationStatus.inProgress) {
          clearInterval(this.TranslationSchedulingIntervalId);
          this.TranslationInProgress = false;
        }

        if (this.PendingTranslation.pendingTranslationStatus != PendingTranslationStatus.done)
          return;

        this.TranslatedContent = pendingTranslation.automaticContentTranslation;
        this.TranslatedTitle = pendingTranslation.automaticTitleTranslation;
      });

    }, 5000);

  }

  public Preview(form: FormGroup) {
    this.alertService.removeAll();
    if (!form.valid) {
      this.alertService.add("danger", "There are some errors in the form.");
      return;
    }
    window.scrollTo(0, 0);
    this.PreviewMode = true;
    this.EditionMode = false;
  }

  public Edit() {
    this.PreviewMode = false;
    this.EditionMode = true;
  }

  public SaveDraft() {
    this.SavingAsDraft = true;
    
    if (!this.validFields()) {
      this.alertService.add("danger", "There are some errors in the form.");
      this.SavingAsDraft = false;
      return
    }
    
    this.getDraftRequest().then((translated) => {
      if (translated.IsDraft) {
        const updateObs = this.postService.UpdateDraft(this.CurrentLanguage, translated);
        
        updateObs.subscribe(
          (p) => this.SavingAsDraft = false,
          (err) => 
          {
            this.alertService.add( "danger", "Post translation could not be saved as draft"); 
            this.SavingAsDraft = false;
          }
        );

      } else {
        translated.IsDraft = true;

        this.postService.CreateDraft(this.CurrentLanguage, translated).subscribe(
          (post) => {
            this.postTranslationsService.UpdateDraftId(this.CurrentLanguage, this.SourcePost.Id, post.Id, this.authService.getActualLanguage().languageId)
              .subscribe(
                (translation) => {
                  this.DraftId = post.Id;
                  this.IsDraft = true;
                  this.SavingAsDraft = false;

                  this.alertService.add("success", "Draft saved");
                },
                (err) => {
                  this.alertService.add("danger", "Post translation could not be saved as draft");
                  this.SavingAsDraft = false
                }
              );
          },
          (err) => {
            this.alertService.add("danger", "Post translation could not be saved as draft");
            this.SavingAsDraft = false;
          });
      }
    });
  }

  public Publish() {
    this.CurrentlySendingPost = true;
    
    this.getPostRequest().then((translated) => {
      this.alertService.removeAll();
      window.scrollTo(0, 0);
      
      this.postService.CreatePost(this.CurrentLanguage, translated).subscribe(
        (post) => {
          this.createTranslationRecordFromPending(post);
          this.IsDraft = false;
        },
        (err) => {
          this.alertService.add("danger", "Post translation could not be created");
          this.CurrentlySendingPost = false;
        }
      );
    });
  }

  private createTranslationRecordFromPending(post) {

    var translationRecord: TranslationRecord =  {
      postId: post.Id,
      publishedContentTranslation: post.Content,
      publishedTitleTranslation: post.Title,
      translationDateUtc: post.PublicationDate,
    };

    this.postTranslationsService
      .CreateTranslationRecord(
        this.CurrentLanguage,
        this.SourcePost.Id,
        translationRecord
      )
      .subscribe(
        (translation) => {
          this.PreviewMode = false;
          this.EditionMode = false;
          this.PostCreatedMode = true;

          this.PostCreatedLink = post.SeoUrl;
          this.alertService.add(
            "success",
            "Post translation successfully created"
          );
        },
        (err) => {
          this.alertService.add(
            "danger",
            "The post was created but could not be marked as translated. Please check."
          );
        }
      );
  }

  private async getDraftRequest(): Promise<DraftSeo> {
    var tagsList = this.postTags.map((t) => t.Id);
    const result: DraftSeo = {
      Id: this.IsDraft ? this.DraftId : null,
      Title: this.TranslatedTitle,
      Url: null,
      CompanyId: this.PostTranslationsConfig.DefaultCompanyId,
      AuthorId: this.authorSelected ? this.authorSelected.AuthorId : this.PostTranslationsConfig.DefaultAuthorId,
      FeedId: this.SourcePost.FeedId,
      LanguageId: this.authService.getActualLanguage().languageId,
      Content: this.TranslatedContent,
      Tags: tagsList,
      ImageUrl: this.SourcePost.ImageUrl,
      ImageCaption: this.SourcePost.ImageCaption,
      ImageCopyright: this.SourcePost.ImageCopyright,
      SeoMetaTitle: this.TranslatedTitle,
      SeoMetaDescription: this.GetPostSeoMetaDescription(),
      SeoKeyWords: await this.GetTranslatedKeyWords(this.postTags),
      SeoUrl: null,
      PublicationDate: null,
      CreationDate: null,
      IsDraft: this.IsDraft,
      Type: 1,
      Status: "1"
    };

    return result;
  }

  private async getPostRequest(): Promise<PostSeo> {
    var tagsList = this.postTags.map((t) => t.Id);
    const result: PostSeo = {
      Id: this.IsDraft ? this.DraftId : null,
      Title: this.TranslatedTitle,
      Url: null,
      CompanyId: this.PostTranslationsConfig.DefaultCompanyId,
      AuthorId: this.authorSelected ? this.authorSelected.AuthorId : this.PostTranslationsConfig.DefaultAuthorId,
      FeedId: this.SourcePost.FeedId,
      LanguageId: this.authService.getActualLanguage().languageId,
      Content: this.TranslatedContent,
      Tags: tagsList,
      ImageUrl: this.SourcePost.ImageUrl,
      ImageCaption: this.SourcePost.ImageCaption,
      ImageCopyright: this.SourcePost.ImageCopyright,
      SeoMetaTitle: this.TranslatedTitle,
      SeoMetaDescription: this.GetPostSeoMetaDescription(),
      SeoKeyWords: await this.GetTranslatedKeyWords(this.postTags),
      SeoUrl: null,
      PublicationDate: null,
      CreationDate: null,
      IsDraft: this.IsDraft,
      TranslationStatus: TranslationStatus.Verified,
      TranslationSourceId: this.SourcePost.Id
    };

    return result;
  }

  private async GetTranslatedKeyWords(tags: Tag[]): Promise<string> {
    return tags.map(t => t.Name).join();
  }

  private GetPostSeoMetaDescription(): string {

    const div = document.createElement("div");
    div.innerHTML = this.TranslatedContent;
    
    const pElement = div.getElementsByTagName("p");
    let firstParagraph = "";

    if (pElement.length > 0)
      firstParagraph = pElement[0].innerText;

    const result = firstParagraph.substring(0, 512);
    return result;
  }

  private SetWYSWYGLanguage() {
    window["fxs_widget_config"].Culture = this.CurrentLanguage;
    if (window["FXStreetWidgets"] != null)
      window["FXStreetWidgets"].Configuration.loadCustomConfig();
  }

  private validFields() {
    return this.TranslatedTitle && this.TranslatedContent && this.postTags.length !== 0;
  }
}



// WEBPACK FOOTER //
// ./src/app/postTranslations/components/scripts/postTranslation.component.ts